import { i18n } from '../i18n/index.svelte';
import { enqueueToast, type Toast, type ToastInput } from './toastQueue';

/**
 * The one place the app announces something to the player.
 *
 * Before this, a toast was local state inside whichever view happened to need
 * it, which meant it could only appear on that screen and vanished the moment
 * the route changed. Half the events worth announcing — "the server dropped",
 * "the code expired", "your opponent left" — happen *during* a navigation, so
 * they were exactly the ones that could never be shown. A store outside the
 * view tree fixes that.
 *
 * The queue rules (coalescing, eviction, per-kind lifetimes) live in
 * `toastQueue.ts`; what is here is reactivity and timers.
 */

export type { Toast, ToastAction, ToastInput, ToastKind } from './toastQueue';

let nextId = 1;

class Toasts {
  items = $state<Toast[]>([]);

  private timers = new Map<number, number>();

  /**
   * Show a toast. Returns its id so a caller that owns a long-lived condition
   * — a reconnect in progress, say — can dismiss it when the condition clears.
   */
  push(input: ToastInput): number {
    const result = enqueueToast(this.items, input, nextId);
    if (!result.coalesced) nextId += 1;

    for (const id of result.evicted) this.clearTimer(id);
    this.items = [...result.items];
    this.arm(result.toast.id, result.toast.durationMs);

    return result.toast.id;
  }

  dismiss(id: number): void {
    this.clearTimer(id);
    this.items = this.items.filter((toast) => toast.id !== id);
  }

  /** Dismiss whatever is currently showing under `key`, if anything. */
  dismissKey(key: string): void {
    const existing = this.items.find((toast) => toast.key === key);
    if (existing) this.dismiss(existing.id);
  }

  clear(): void {
    for (const toast of this.items) this.clearTimer(toast.id);
    this.items = [];
  }

  // --- semantic shorthands ---------------------------------------------------

  success(message: string, options: Partial<ToastInput> = {}): number {
    return this.push({ ...options, kind: 'success', message });
  }

  info(message: string, options: Partial<ToastInput> = {}): number {
    return this.push({ ...options, kind: 'info', message });
  }

  warning(message: string, options: Partial<ToastInput> = {}): number {
    return this.push({ ...options, kind: 'warning', message });
  }

  error(message: string, options: Partial<ToastInput> = {}): number {
    return this.push({ ...options, kind: 'error', message });
  }

  private arm(id: number, durationMs: number): void {
    this.clearTimer(id);
    if (durationMs <= 0) return;

    // Reduced motion shortens the *animation*, never the reading time — the
    // dismiss timer is deliberately not passed through `dur()`.
    this.timers.set(
      id,
      window.setTimeout(() => this.dismiss(id), durationMs),
    );
  }

  private clearTimer(id: number): void {
    const handle = this.timers.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      this.timers.delete(id);
    }
  }
}

export const toasts = new Toasts();

/**
 * Achievement unlocks, worded the same way in every mode.
 *
 * One toast even when several unlock at once: they arrive together at the end
 * of a match, and three stacked cards would sit on top of the result screen the
 * player is trying to read. Lives here rather than in a view because Solo,
 * Versus and every future mode announce them identically.
 */
export function announceAchievements(ids: readonly string[]): void {
  const items = i18n.t.achievements.items as Record<string, { name: string }>;
  const names = ids.map((id) => items[id]?.name).filter((name): name is string => Boolean(name));
  if (names.length === 0) return;

  const label = names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]!;
  toasts.success(i18n.t.toasts.achievementsUnlocked(label), { durationMs: 4500 });
}
